import React, { useState } from "react";
import axios from "axios";
import { BLUEPRINT } from "@/constants/testIds";
import { X, Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteModal({ blueprintId, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (sending || done) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quote`, { blueprint_id: blueprintId, ...form });
      setSuccessMsg(data?.message || "");
      setDone(true);
    } catch (err) {
      console.error(err);
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/30 backdrop-blur-[2px] flex items-end md:items-center justify-center px-0 md:px-6"
      onClick={onClose}
      data-testid={BLUEPRINT.quoteModal}
    >
      <div
        className="bg-white w-full md:max-w-[560px] rounded-t-3xl md:rounded-3xl p-7 md:p-10 shadow-bnm border border-bnm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="eyebrow eyebrow-denim mb-2">Request a Quote</div>
            <h3 className="font-display text-[26px] md:text-[30px] tracking-[-0.025em] text-ink leading-[1.1]">
              Let&rsquo;s bring it to{" "}
              <span className="font-serif-italic font-light text-denim-bnm">life</span>.
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="w-9 h-9 rounded-full border border-bnm flex items-center justify-center hover:border-black"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="py-6" data-testid={BLUEPRINT.quoteSuccess}>
            <div className="w-12 h-12 rounded-full bg-denim-bnm/10 text-denim-bnm flex items-center justify-center mb-5">
              <Check className="w-6 h-6" />
            </div>
            <p className="font-display text-[22px] md:text-[24px] tracking-[-0.02em] text-ink leading-[1.25] mb-3">
              Thank you.
            </p>
            <p className="text-muted-ink text-[15px] leading-[1.65]">
              {successMsg || (
                <>Your Producer at BRAND NEW MONEY FILMS is reviewing your blueprint. We&rsquo;ll be in touch shortly with a tailored proposal.</>
              )}
            </p>
            <button
              type="button"
              className="btn-ghost mt-7 w-full md:w-auto"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow eyebrow-muted block mb-2">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                data-testid={BLUEPRINT.quoteName}
              />
            </div>
            <div>
              <label className="eyebrow eyebrow-muted block mb-2">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@studio.com"
                className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                data-testid={BLUEPRINT.quoteEmail}
              />
            </div>
            <div>
              <label className="eyebrow eyebrow-muted block mb-2">Phone (optional)</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1 555 123 4567"
                className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                data-testid={BLUEPRINT.quotePhone}
              />
            </div>
            <div>
              <label className="eyebrow eyebrow-muted block mb-2">Notes for your Producer</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Timing, budget cap, must-haves…"
                className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors resize-none"
                data-testid={BLUEPRINT.quoteNotes}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="btn-denim w-full"
              data-testid={BLUEPRINT.quoteSubmit}
            >
              <span>{sending ? "Sending…" : "Send to Your Producer"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
